import React, { useEffect, useMemo, useState } from 'react';
import type { ResponsePayload } from '../../../types';
import { StyledBooleanQuestion } from '../../questions/StyledBooleanQuestion';
import { registerQuestionRenderer, type QuestionRendererComponent } from './registry';

const BooleanRenderer: QuestionRendererComponent = ({
  question,
  existingResponse,
  isQuizFlow,
  onDraftChange,
  onHasUserInputChange,
  onValidityChange,
  onExtrasChange,
}) => {
  void onExtrasChange;
  const questionId = question?.id ?? existingResponse?.question ?? -1;

  const initialValue = useMemo(() => {
    if (typeof existingResponse?.boolean_value === 'boolean') {
      return existingResponse.boolean_value;
    }
    return null;
  }, [existingResponse]);

  const [value, setValue] = useState<boolean | null>(initialValue);

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue, questionId]);

  useEffect(() => {
    const hasInput = value !== null;
    onHasUserInputChange?.(hasInput);
    onValidityChange?.(hasInput);

    if (!hasInput) {
      onDraftChange(null);
      return;
    }

    const payload: ResponsePayload = {
      question: questionId,
      boolean_value: value,
      text_value: value ? 'true' : 'false',
    };

    onDraftChange(payload);
  }, [onDraftChange, onHasUserInputChange, onValidityChange, questionId, value]);

  const handleChange = (nextValue: boolean) => {
    setValue(nextValue);
  };

  return (
    <StyledBooleanQuestion
      question={question}
      booleanValue={value}
      showAnswerFeedback={!isQuizFlow}
      onChange={handleChange}
    />
  );
};

registerQuestionRenderer(['boolean'], BooleanRenderer);
